/**
 * Cart Configured Image Renderer
 * Renders configured safe images in cart based on selections stored in cart properties
 * Uses the product's image_manifest metaobject to layer images
 */

class CartConfiguredImageRenderer {
  constructor() {
    this.imageCache = new Map();
  }

  /**
   * Render configured images for all cart items. Each configured item's image element
   * carries its own manifest inline (data-image-manifest, set in
   * snippets/cart-configured-image-markup.liquid) instead of relying on a single
   * page-scoped manifest global — that global didn't exist in the ajax cart drawer at all,
   * and would silently get overwritten by the last-matched item when a cart held more than
   * one configured product with different manifests.
   */
  async renderCartImages(cartData) {
    console.log('🎨 Starting cart image rendering...');
    console.log('📦 Cart items:', cartData.items?.length || 0);

    if (!cartData.items || cartData.items.length === 0) {
      console.log('⚠️ No cart items to render');
      return;
    }

    // Find all configured items (those with _configuredProduct property)
    const configuredItems = cartData.items.filter(item =>
      item.properties && item.properties._configuredProduct === 'true'
    );

    console.log('🎨 Found', configuredItems.length, 'configured items');

    for (const item of configuredItems) {
      await this.renderItemImage(item);
    }
  }

  /**
   * Render a single cart item's configured image
   */
  async renderItemImage(item) {
    try {
      const imgEl = document.querySelector(`img.cart-configured-safe-image[data-cart-item-key="${item.key}"]`);
      if (!imgEl) {
        console.warn('⚠️ Could not find cart image element for key:', item.key);
        return;
      }
      if (imgEl.dataset.rendered === 'true') {
        return; // Already rendered for this element instance
      }

      console.log('🖼️ Rendering image for item:', item.product_title);

      const properties = item.properties || {};

      // Extract selections from cart properties
      const selections = this.extractSelectionsFromProperties(properties);
      console.log('📋 Extracted selections:', selections);

      if (!selections.size) {
        console.warn('⚠️ No size found in selections');
        return;
      }

      // Manifest is inlined on the image element itself (see class doc comment above)
      if (!imgEl.dataset.imageManifest) {
        console.warn('⚠️ No manifest found for product:', item.product_title);
        return;
      }

      let manifest;
      try {
        manifest = JSON.parse(imgEl.dataset.imageManifest);
      } catch (e) {
        console.warn('⚠️ Failed to parse image manifest for product:', item.product_title, e);
        return;
      }
      manifest.colors = window.cartImageColorMap || {};

      console.log('✅ Found manifest for product');

      // Render the image
      const canvas = await this.layerImages(selections, manifest);
      
      if (!canvas) {
        console.warn('⚠️ Failed to create canvas');
        return;
      }

      // Convert canvas to data URL
      const dataUrl = canvas.toDataURL('image/png', 0.9);

      // Find the cart item image element and replace it
      this.replaceCartImage(item.key, dataUrl);

      console.log('✅ Rendered configured image for:', item.product_title);
    } catch (error) {
      console.error('❌ Error rendering item image:', error);
    }
  }

  /**
   * Extract configuration selections from cart properties
   */
  extractSelectionsFromProperties(properties) {
    const selections = {};
    
    // Map property names to selection keys
    const propertyMap = {
      'Size': 'size',
      'Exterior Finish': 'exterior_finish',
      'Color (Single)': 'color_single',
      'Body Color': 'body_color',
      'Door Color': 'door_color',
      'Lock Type': 'lock_type',
      'Lock/Graphic Color': 'lockgraphic_color',
      'Graphic': 'graphic',
      'Hinge Type': 'hinge_type',
      'Hinge Swing': 'hinge_swing',
      'Rivet Color': 'rivet_color',
      'Crane Hinge Color': 'crane_hinge_color'
    };

    for (const [displayName, key] of Object.entries(propertyMap)) {
      if (properties[displayName]) {
        // Size needs special formatting to match manifest keys
        if (key === 'size') {
          selections[key] = this.convertSizeToManifestFormat(properties[displayName]);
        } else if (key === 'hinge_type') {
          // Normalize hinge type to match manifest structure
          selections[key] = this.normalizeHingeType(this.convertToCode(properties[displayName]));
        } else {
          selections[key] = this.convertToCode(properties[displayName]);
        }
      }
    }
    
    // Extract accessories (check for RIVET_TRIM, PULL_HANDLE, etc.)
    selections.accessories = [];
    for (const [key, value] of Object.entries(properties)) {
      // Accessories are stored with 'Accessory:' prefix or just the name
      if (key.includes('Accessory') || key.includes('Rivet') || key.includes('Pull') || key.includes('Hinge Cap')) {
        const accessoryCode = this.convertToCode(value);
        if (accessoryCode && accessoryCode !== 'NONE') {
          selections.accessories.push(accessoryCode);
        }
      }
    }

    return selections;
  }

  /**
   * Convert size display name to manifest format
   * E.g., '72" x 41" x 27"' -> '7241x27'
   * E.g., '72" x 51" x 40" XD' -> '7251x40XD'
   * Format: concatenate first two dimensions (HxW) + 'x' + depth + optional suffix
   */
  convertSizeToManifestFormat(sizeDisplay) {
    console.log('🔧 Converting size format:', sizeDisplay);
    
    // First, extract any suffix (letters at the end like "XD")
    // Pattern: look for letters at the very end that are not part of a dimension
    const suffixMatch = sizeDisplay.match(/\s+([A-Za-z]+)\s*$/);
    const suffix = suffixMatch ? suffixMatch[1].toUpperCase() : '';
    
    // Remove the suffix for dimension parsing
    let dimensionPart = sizeDisplay;
    if (suffix) {
      dimensionPart = sizeDisplay.replace(/\s+[A-Za-z]+\s*$/, '');
    }
    
    // Remove quotes and split by 'x'
    const parts = dimensionPart
      .replace(/"/g, '')
      .replace(/\s+/g, '')
      .toLowerCase()
      .split('x');
    
    // If we have 3 dimensions, concatenate first two, then add x + third + suffix
    // E.g., ['72', '51', '40'] with suffix 'XD' -> '7251x40XD'
    if (parts.length === 3) {
      const result = `${parts[0]}${parts[1]}x${parts[2]}${suffix}`;
      console.log('✅ Size format converted:', sizeDisplay, '->', result);
      return result;
    }
    
    // Fallback: return as-is if format is unexpected
    console.warn('⚠️ Unexpected size format:', sizeDisplay, 'parts:', parts);
    return sizeDisplay.replace(/"/g, '').replace(/\s+/g, '').toLowerCase();
  }

  /**
   * Convert display name to code format
   * E.g., "Mercedes Silver" -> "MERCEDES_SILVER"
   */
  convertToCode(displayName) {
    return displayName
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  }

  /**
   * Normalize hinge swing from cart property to match manifest structure
   * E.g., "LEFT_SWING" -> "LEFT", "RIGHT_SWING" -> "RIGHT"
   */
  normalizeHingeSwing(hingeSwing) {
    const normalizationMap = {
      'LEFT_SWING': 'LEFT',
      'RIGHT_SWING': 'RIGHT'
    };
    
    const normalized = normalizationMap[hingeSwing] || hingeSwing;
    console.log('🔧 Normalized hinge swing:', hingeSwing, '->', normalized);
    return normalized;
  }

  /**
   * Normalize hinge type from cart property to match manifest structure
   * E.g., "CRANE_HINGE" -> "CRANE", "BLANK_CRANE_HINGE" -> "CRANE_BLANK"
   */
  normalizeHingeType(hingeType) {
    const normalizationMap = {
      'CRANE_HINGE': 'CRANE',
      'BLANK_CRANE_HINGE': 'CRANE_BLANK',
      'EXTERNAL_HINGE': 'EXTERNAL',
      'INTERNAL_HINGE': 'INTERNAL'
    };
    
    const normalized = normalizationMap[hingeType] || hingeType;
    console.log('🔧 Normalized hinge type:', hingeType, '->', normalized);
    return normalized;
  }

  /**
   * Layer images based on selections and manifest
   */
  async layerImages(selections, manifest) {
    // Stashed for drawColoredMask's getColorHex lookup, which has no manifest parameter of
    // its own — see the class doc comment for why this replaced a window.cartImageManifest
    // global.
    this.currentManifest = manifest;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const size = selections.size;
    if (!size) {
      console.error('❌ No size in selections');
      return null;
    }
    
    // Set canvas size dynamically based on safe size proportions
    // Parse size code to get dimensions (e.g., '7241x27' -> 72" H x 41" W)
    const sizeMatch = size.match(/(\d+)(\d{2})x(\d+)/);
    let aspectRatio = 1.5; // Default aspect ratio (height / width)
    
    if (sizeMatch) {
      const height = parseInt(sizeMatch[1]);
      const width = parseInt(sizeMatch[2]);
      aspectRatio = height / width;
      console.log('📐 Calculated aspect ratio:', aspectRatio.toFixed(2), 'from', height, 'x', width);
    }
    
    // Base width of 800px, height calculated from aspect ratio
    canvas.width = 800;
    canvas.height = Math.round(800 * aspectRatio);
    
    console.log('🎨 Canvas dimensions:', canvas.width, 'x', canvas.height, 'for size:', size);

    try {
      // Layer 1: Body color/texture
      const exteriorFinish = selections.exterior_finish;
      const bodyColor = selections.body_color || selections.color_single; // Single color products use color_single
      
      console.log('📍 Layer 1 - Body color/texture:', { 
        exteriorFinish, 
        bodyColor,
        isTwoTone: exteriorFinish === 'TWO_TONE',
        hasBodyMask: !!manifest.masks?.bodyMasks?.[size]
      });

      if (exteriorFinish === 'DISTRESSED' || exteriorFinish === 'DISTRESSED_FINISH') {
        // For distressed, use the pre-made distressed base image
        const distressedBase = manifest.baseImages?.[size]?.distressed;
        if (distressedBase) {
          await this.drawImage(ctx, distressedBase, 0, 0, canvas.width, canvas.height);
          console.log('✅ Drew distressed base');
        } else {
          console.warn('⚠️ Distressed base image not found for size:', size, 'Available sizes:', Object.keys(manifest.baseImages || {}));
        }
      } else if ((exteriorFinish === 'TEXTURED' || exteriorFinish === 'TEXTURE_FINISH') && bodyColor) {
        // For textured finish, use body texture masked
        if (manifest.textures?.bodyTextures?.[bodyColor]?.[size]) {
          await this.drawMaskedTexture(ctx, 
            manifest.masks?.bodyMasks?.[size], 
            manifest.textures.bodyTextures[bodyColor][size], 
            canvas.width, 
            canvas.height
          );
          console.log('✅ Drew textured body:', bodyColor, 'URL:', manifest.textures.bodyTextures[bodyColor][size]);
        } else {
          console.warn('⚠️ Textured body not found for:', { bodyColor, size, exteriorFinish, availableTextures: Object.keys(manifest.textures?.bodyTextures || {}) });
        }
      } else if (bodyColor && manifest.masks?.bodyMasks?.[size]) {
        // For GLOSS_FINISH or TWO_TONE, apply solid color to body mask
        await this.drawColoredMask(ctx, 
          manifest.masks.bodyMasks[size], 
          bodyColor, 
          canvas.width, 
          canvas.height
        );
        console.log('✅ Drew body with color:', bodyColor);
      } else {
        console.warn('⚠️ Could not draw body for:', { bodyColor, size, exteriorFinish });
      }

      // Layer 2: Door color/texture
      const doorColor = selections.door_color || selections.color_single;
      console.log('📍 Layer 2 - Door color/texture:', { 
        doorColor,
        exteriorFinish,
        hasDoorMask: !!manifest.masks?.doorMasks?.[size]
      });

      // Skip door for distressed (it's already in the base image)
      if (exteriorFinish !== 'DISTRESSED' && exteriorFinish !== 'DISTRESSED_FINISH') {
        if ((exteriorFinish === 'TEXTURED' || exteriorFinish === 'TEXTURE_FINISH') && doorColor) {
          // For textured finish, use door texture masked
          if (manifest.textures?.doorTextures?.[doorColor]?.[size]) {
            await this.drawMaskedTexture(ctx, 
              manifest.masks?.doorMasks?.[size], 
              manifest.textures.doorTextures[doorColor][size], 
              canvas.width, 
              canvas.height
            );
            console.log('✅ Drew textured door:', doorColor, 'URL:', manifest.textures.doorTextures[doorColor][size]);
          } else {
            console.warn('⚠️ Textured door not found for:', { doorColor, size, exteriorFinish, availableTextures: Object.keys(manifest.textures?.doorTextures || {}) });
          }
        } else if (doorColor && manifest.masks?.doorMasks?.[size]) {
          // For GLOSS_FINISH or TWO_TONE, apply solid color to door mask
          await this.drawColoredMask(ctx, 
            manifest.masks.doorMasks[size], 
            doorColor, 
            canvas.width, 
            canvas.height
          );
          console.log('✅ Drew door with color:', doorColor);
        } else {
          console.warn('⚠️ Could not draw door for:', { doorColor, size, exteriorFinish });
        }
      }

      // Layer 3: Lock/Handle (scaled for cart display with manifest positioning)
      const lockType = selections.lock_type;
      const lockColor = selections.lockgraphic_color;
      if (lockType && lockColor) {
        const lockImage = manifest.locks?.[lockType]?.[lockColor];
        if (lockImage) {
          // Use dynamic scaling based on safe size, but moderate for cart (0.6x instead of 1.2x)
          const referenceSize = manifest.referenceSize || '4026x20';
          const lockScaleFactor = this.calculateAccessoryScale(size, referenceSize) * 0.6;
          
          // Get lock position from manifest (per-size, not per-lock-type)
          let lockTopOffset = manifest.lockPosition?.[size]?.top || '0%';
          
          // Special handling for REDUNDANT lock - move it down more since it's taller
          if (lockType === 'REDUNDANT') {
            // Parse the current offset and add 8% more downward offset
            const currentOffset = parseFloat(lockTopOffset) || 0;
            lockTopOffset = `${currentOffset + 8}%`;
            console.log('🔒 REDUNDANT lock: adjusted offset from', manifest.lockPosition?.[size]?.top, 'to', lockTopOffset);
          }
          
          // Draw lock with calculated scale and position
          await this.drawImageWithScale(ctx, lockImage, canvas.width, canvas.height, lockScaleFactor, lockTopOffset);
          console.log('✅ Drew lock:', lockType, lockColor, 'scale:', lockScaleFactor.toFixed(2), 'offset:', lockTopOffset);
        } else {
          console.warn('⚠️ Lock image not found:', { lockType, lockColor });
        }
      }

      // Layer 4: Graphic (check for rivet trim to determine which version to use)
      const graphic = selections.graphic;
      const lockGraphicColor = selections.lockgraphic_color;
      const hasRivetTrim = selections.accessories?.includes('RIVET_TRIM');
      
      // Get rivet color override (defaults to body color if not specified)
      const rivetColor = selections.rivet_color || bodyColor;
      
      if (graphic && graphic !== 'NONE' && graphic !== 'NO_GRAPHIC') {
        // Check for RIVETS version if rivet trim is selected
        let graphicImage = null;
        let graphicType = 'GRAYSCALE';
        
        if (hasRivetTrim && manifest.graphics?.[graphic]?.RIVETS?.[size]) {
          // Use RIVETS version if available and rivet trim is selected
          graphicImage = manifest.graphics[graphic].RIVETS[size];
          graphicType = 'RIVETS';
          console.log('📍 Using RIVETS graphic version (rivet trim selected)');
        } else {
          // Use GRAYSCALE version
          graphicImage = manifest.graphics?.[graphic]?.GRAYSCALE?.[size];
          graphicType = 'GRAYSCALE';
        }
        
        if (graphicImage) {
          // LOGO_ONLY stays pre-colored, others get color masking
          if (graphic === 'LOGO_ONLY') {
            await this.drawImage(ctx, graphicImage, 0, 0, canvas.width, canvas.height);
            console.log('✅ Drew pre-colored graphic:', graphic);
          } else if (lockGraphicColor) {
            // Apply color masking like we do for body/door
            await this.drawColoredMask(ctx, graphicImage, lockGraphicColor, canvas.width, canvas.height);
            console.log('✅ Drew graphic with color:', graphic, graphicType, lockGraphicColor);
          } else {
            // No color specified, draw grayscale
            await this.drawImage(ctx, graphicImage, 0, 0, canvas.width, canvas.height);
            console.log('✅ Drew grayscale graphic:', graphic, graphicType);
          }
        }
      }
      
      // Layer 4b: Rivet Trim (separate overlay if rivet trim accessory selected)
      // Only needed when using GRAYSCALE graphic or when rivet color differs from graphic color
      if (hasRivetTrim && rivetColor) {
        // Check if we need separate rivet rendering (either not using RIVETS graphic, or custom rivet color)
        const needsSeparateRivets = selections.rivet_color || 
                                    !manifest.graphics?.[graphic]?.RIVETS?.[size] ||
                                    graphic === 'NONE' || graphic === 'NO_GRAPHIC' || !graphic;
        
        if (needsSeparateRivets && manifest.rivetTrim?.images?.GRAYSCALE?.[size]) {
          const rivetImage = manifest.rivetTrim.images.GRAYSCALE[size];
          await this.drawColoredMask(ctx, rivetImage, rivetColor, canvas.width, canvas.height);
          console.log('✅ Drew rivet trim overlay with color:', rivetColor);
        }
      }

      // Layer 5: Hinges (EXTERNAL, CRANE, or CRANE_BLANK)
      const hingeType = selections.hinge_type;
      const hingeSwing = selections.hinge_swing;
      
      // Get crane hinge color override (defaults to body color if not specified)
      const craneHingeColor = selections.crane_hinge_color || bodyColor;
      
      console.log('📍 Layer 5 - Hinges:', { hingeType, hingeSwing, size, craneHingeColor });
      console.log('  ↳ Hinge manifest available:', !!manifest.hinges);
      console.log('  ↳ Hinge images:', Object.keys(manifest.hinges?.images || {}));
      
      if (hingeType && hingeType !== 'INTERNAL') {
        console.log('  ↳ Processing hinge type:', hingeType);
        if (hingeType === 'EXTERNAL') {
          // External hinges - use texture if textured finish, otherwise use body color
          const hingeImage = manifest.hinges?.images?.EXTERNAL?.[size]?.grayscale;
          console.log('  ↳ EXTERNAL hinge image URL:', hingeImage);
          console.log('  ↳ Exterior finish:', exteriorFinish);
          console.log('  ↳ Body color:', bodyColor);
          
          if (hingeImage) {
            if (exteriorFinish === 'DISTRESSED' || exteriorFinish === 'DISTRESSED_FINISH') {
              // For distressed finish, just draw grayscale mask (distressed texture already on safe)
              await this.drawImage(ctx, hingeImage, 0, 0, canvas.width, canvas.height);
              console.log('✅ Drew external hinges (distressed - grayscale mask)');
            } else if ((exteriorFinish === 'TEXTURED' || exteriorFinish === 'TEXTURE_FINISH') && bodyColor && manifest.hinges?.textures?.[bodyColor]) {
              // For textured finish, use the texture as a mask on the hinge
              const hingeTexture = manifest.hinges.textures[bodyColor];
              await this.drawMaskedTexture(ctx, hingeImage, hingeTexture, canvas.width, canvas.height);
              console.log('✅ Drew external hinges with texture:', bodyColor);
            } else if (bodyColor) {
              // For non-textured finish, apply body color to hinges
              await this.drawColoredMask(ctx, hingeImage, bodyColor, canvas.width, canvas.height);
              console.log('✅ Drew external hinges with body color:', bodyColor);
            } else {
              // No color specified, draw grayscale
              await this.drawImage(ctx, hingeImage, 0, 0, canvas.width, canvas.height);
              console.log('✅ Drew external hinges (grayscale)');
            }
          } else {
            console.warn('⚠️ EXTERNAL hinge image not found for size:', size);
          }
          
          // Layer 5b: Hinge Caps (if external hinges with caps)
          const hingeCapImage = manifest.hingeCaps?.images?.EXTERNAL?.[size]?.grayscale;
          if (hingeCapImage && lockGraphicColor) {
            // Apply lock/graphic color to hinge caps
            await this.drawColoredMask(ctx, hingeCapImage, lockGraphicColor, canvas.width, canvas.height);
            console.log('✅ Drew hinge caps with lock/graphic color:', lockGraphicColor);
          }
        } else if (hingeType === 'CRANE' && hingeSwing) {
          // CRANE hinges - multi-layer system (BASE + colored layers from CRANE_BLANK)
          const swingUpper = this.normalizeHingeSwing(hingeSwing);
          
          // Get the base image from CRANE structure (has LEFT/RIGHT variants) - or use CRANE_BLANK BASE
          const craneBaseImage = manifest.hinges?.images?.CRANE?.GRAYSCALE?.[swingUpper]?.[size];
          
          // Get colored layers from CRANE_BLANK LEFT structure (always LEFT, will mirror for RIGHT)
          const craneBlankConfig = manifest.hinges?.images?.CRANE_BLANK?.GRAYSCALE?.LEFT?.[size];
          const shouldFlipColoredLayers = (swingUpper === 'RIGHT');
          
          console.log('  ↳ CRANE hinge lookup:', { swing: swingUpper, size, shouldFlipColoredLayers });
          console.log('  ↳ CRANE base image URL:', craneBaseImage);
          console.log('  ↳ CRANE_BLANK config for colors:', craneBlankConfig);
          console.log('  ↳ Crane hinge color from selections:', craneHingeColor);
          
          if (craneBaseImage || craneBlankConfig?.BASE) {
            // Layer 1: BASE (colored with crane hinge color, or textured, or grayscale for distressed)
            // Use CRANE base if available, otherwise use CRANE_BLANK BASE
            const baseImage = craneBaseImage || craneBlankConfig?.BASE;
            if (exteriorFinish === 'DISTRESSED' || exteriorFinish === 'DISTRESSED_FINISH') {
              // For distressed, use grayscale mask only
              await this.drawImage(ctx, baseImage, 0, 0, canvas.width, canvas.height, shouldFlipColoredLayers);
              console.log('  ↳ Drew CRANE base (distressed - grayscale mask)', swingUpper, shouldFlipColoredLayers ? '(flipped)' : '');
            } else if ((exteriorFinish === 'TEXTURED' || exteriorFinish === 'TEXTURE_FINISH') && craneHingeColor && manifest.hinges?.textures?.[craneHingeColor]) {
              // For textured finish, use the texture as a mask on the hinge
              const hingeTexture = manifest.hinges.textures[craneHingeColor];
              await this.drawMaskedTexture(ctx, baseImage, hingeTexture, canvas.width, canvas.height);
              console.log('  ↳ Drew CRANE base with texture:', craneHingeColor, swingUpper, shouldFlipColoredLayers ? '(flipped)' : '');
            } else if (baseImage && craneHingeColor) {
              await this.drawColoredMask(ctx, baseImage, craneHingeColor, canvas.width, canvas.height, shouldFlipColoredLayers);
              console.log('  ↳ Drew CRANE base with crane color:', craneHingeColor, swingUpper, shouldFlipColoredLayers ? '(flipped)' : '');
            } else if (baseImage) {
              await this.drawImage(ctx, baseImage, 0, 0, canvas.width, canvas.height, shouldFlipColoredLayers);
              console.log('  ↳ Drew CRANE base (grayscale)', swingUpper, shouldFlipColoredLayers ? '(flipped)' : '');
            }
            
            // Layer 2: BODY_COLOR (from CRANE_BLANK, colored/textured with body/door color, mirrored for RIGHT)
            // Skip for distressed finish
            if (!(exteriorFinish === 'DISTRESSED' || exteriorFinish === 'DISTRESSED_FINISH') && craneBlankConfig?.BODY_COLOR && bodyColor) {
              if ((exteriorFinish === 'TEXTURED' || exteriorFinish === 'TEXTURE_FINISH') && manifest.hinges?.textures?.[bodyColor]) {
                const bodyTexture = manifest.hinges.textures[bodyColor];
                await this.drawMaskedTexture(ctx, craneBlankConfig.BODY_COLOR, bodyTexture, canvas.width, canvas.height);
                console.log('  ↳ Drew CRANE body texture:', bodyColor, shouldFlipColoredLayers ? '(flipped)' : '');
              } else {
                await this.drawColoredMask(ctx, craneBlankConfig.BODY_COLOR, bodyColor, canvas.width, canvas.height, shouldFlipColoredLayers);
                console.log('  ↳ Drew CRANE body color:', bodyColor, shouldFlipColoredLayers ? '(flipped)' : '');
              }
            }
            
            // Layer 3: CRANE_COLOR (from CRANE_BLANK, colored/textured with crane hinge color, mirrored for RIGHT)
            // Skip for distressed finish
            if (!(exteriorFinish === 'DISTRESSED' || exteriorFinish === 'DISTRESSED_FINISH') && craneBlankConfig?.CRANE_COLOR && craneHingeColor) {
              if ((exteriorFinish === 'TEXTURED' || exteriorFinish === 'TEXTURE_FINISH') && manifest.hinges?.textures?.[craneHingeColor]) {
                const craneTexture = manifest.hinges.textures[craneHingeColor];
                await this.drawMaskedTexture(ctx, craneBlankConfig.CRANE_COLOR, craneTexture, canvas.width, canvas.height);
                console.log('  ↳ Drew CRANE crane texture:', craneHingeColor, shouldFlipColoredLayers ? '(flipped)' : '');
              } else {
                await this.drawColoredMask(ctx, craneBlankConfig.CRANE_COLOR, craneHingeColor, canvas.width, canvas.height, shouldFlipColoredLayers);
                console.log('  ↳ Drew CRANE crane color:', craneHingeColor, shouldFlipColoredLayers ? '(flipped)' : '');
              }
            }
            
            // Layer 4: CAPS (from CRANE_BLANK, colored with lock/graphic color, mirrored for RIGHT)
            if (craneBlankConfig?.CAPS && lockGraphicColor) {
              await this.drawColoredMask(ctx, craneBlankConfig.CAPS, lockGraphicColor, canvas.width, canvas.height, shouldFlipColoredLayers);
              console.log('  ↳ Drew CRANE caps with lock/graphic color:', lockGraphicColor, shouldFlipColoredLayers ? '(flipped)' : '');
            }
            
            console.log('✅ Drew CRANE hinges:', swingUpper, size);
          } else {
            console.warn('⚠️ CRANE hinge base image not found:', { swing: swingUpper, size });
            console.warn('  ↳ Available swings:', Object.keys(manifest.hinges?.images?.CRANE?.GRAYSCALE || {}));
            console.warn('  ↳ Available sizes for', swingUpper + ':', Object.keys(manifest.hinges?.images?.CRANE?.GRAYSCALE?.[swingUpper] || {}));
          }
        } else if (hingeType === 'CRANE_BLANK' && hingeSwing) {
          // CRANE_BLANK hinges - multi-layer system (BASE, BODY_COLOR, CRANE_COLOR, CAPS)
          const swingUpper = this.normalizeHingeSwing(hingeSwing);
          
          // Check if swing direction exists in manifest, fall back to LEFT if not
          let hingeConfig = manifest.hinges?.images?.CRANE_BLANK?.GRAYSCALE?.[swingUpper]?.[size];
          let shouldFlip = false;
          
          if (!hingeConfig && swingUpper === 'RIGHT') {
            // Manifest may only have LEFT, use that and mirror it
            hingeConfig = manifest.hinges?.images?.CRANE_BLANK?.GRAYSCALE?.LEFT?.[size];
            shouldFlip = true;
            console.log('  ↳ RIGHT not found, using LEFT hinge config and flipping horizontally');
          }
          
          console.log('  ↳ CRANE_BLANK hinge lookup:', { swing: swingUpper, size, shouldFlip });
          console.log('  ↳ CRANE_BLANK manifest structure:', manifest.hinges?.images?.CRANE_BLANK?.GRAYSCALE);
          console.log('  ↳ CRANE_BLANK config:', hingeConfig);
          console.log('  ↳ Crane hinge color from selections:', craneHingeColor);
          
          if (hingeConfig) {
            // Layer 1: BASE (colored with crane hinge color, or grayscale for distressed)
            if (exteriorFinish === 'DISTRESSED' || exteriorFinish === 'DISTRESSED_FINISH') {
              // For distressed, use grayscale mask only
              await this.drawImage(ctx, hingeConfig.BASE, 0, 0, canvas.width, canvas.height, shouldFlip);
              console.log('  ↳ Drew CRANE_BLANK base (distressed - grayscale mask)', shouldFlip ? '(flipped)' : '');
            } else if (hingeConfig.BASE && craneHingeColor) {
              await this.drawColoredMask(ctx, hingeConfig.BASE, craneHingeColor, canvas.width, canvas.height, shouldFlip);
              console.log('  ↳ Drew CRANE_BLANK base with crane color:', craneHingeColor, shouldFlip ? '(flipped)' : '');
            } else if (hingeConfig.BASE) {
              await this.drawImage(ctx, hingeConfig.BASE, 0, 0, canvas.width, canvas.height, shouldFlip);
              console.log('  ↳ Drew CRANE_BLANK base (grayscale)', shouldFlip ? '(flipped)' : '');
            }
            
            // Layer 2: BODY_COLOR (colored with body/door color)
            // Skip for distressed finish
            if (!(exteriorFinish === 'DISTRESSED' || exteriorFinish === 'DISTRESSED_FINISH') && hingeConfig.BODY_COLOR && bodyColor) {
              await this.drawColoredMask(ctx, hingeConfig.BODY_COLOR, bodyColor, canvas.width, canvas.height, shouldFlip);
              console.log('  ↳ Drew CRANE_BLANK body color:', bodyColor, shouldFlip ? '(flipped)' : '');
            }
            
            // Layer 3: CRANE_COLOR (colored with crane hinge color)
            // Skip for distressed finish
            if (!(exteriorFinish === 'DISTRESSED' || exteriorFinish === 'DISTRESSED_FINISH') && hingeConfig.CRANE_COLOR && craneHingeColor) {
              await this.drawColoredMask(ctx, hingeConfig.CRANE_COLOR, craneHingeColor, canvas.width, canvas.height, shouldFlip);
              console.log('  ↳ Drew CRANE_BLANK crane color:', craneHingeColor, shouldFlip ? '(flipped)' : '');
            }
            
            // Layer 4: CAPS (colored with lock/graphic color)
            if (hingeConfig.CAPS && lockGraphicColor) {
              await this.drawColoredMask(ctx, hingeConfig.CAPS, lockGraphicColor, canvas.width, canvas.height, shouldFlip);
              console.log('  ↳ Drew CRANE_BLANK caps with lock/graphic color:', lockGraphicColor, shouldFlip ? '(flipped)' : '');
            }
            
            console.log('✅ Drew CRANE_BLANK hinges:', swingUpper, size);
          } else {
            console.warn('⚠️ CRANE_BLANK hinge config not found:', { swing: swingUpper, size });
            console.warn('  ↳ Available swings:', Object.keys(manifest.hinges?.images?.CRANE_BLANK?.GRAYSCALE || {}));
          }
        }
      }

      console.log('✅ All layers drawn successfully');
      return canvas;

    } catch (error) {
      console.error('❌ Error layering images:', error);
      return null;
    }
  }

  /**
   * Draw an image with scale factor and vertical offset (for locks)
   */
  async drawImageWithScale(ctx, imageUrl, width, height, scaleFactor = 1, topOffset = '0%') {
    return new Promise((resolve, reject) => {
      // Check cache first
      if (this.imageCache.has(imageUrl)) {
        const img = this.imageCache.get(imageUrl);
        this.drawScaledImage(ctx, img, width, height, scaleFactor, topOffset);
        resolve();
        return;
      }

      // Load new image
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        this.imageCache.set(imageUrl, img);
        this.drawScaledImage(ctx, img, width, height, scaleFactor, topOffset);
        resolve();
      };
      
      img.onerror = (err) => {
        console.warn('⚠️ Failed to load image:', imageUrl, err);
        resolve(); // Don't reject, just skip this layer
      };
      
      img.src = imageUrl;
    });
  }

  /**
   * Helper to draw scaled image with offset - preserves aspect ratio
   */
  drawScaledImage(ctx, img, canvasWidth, canvasHeight, scaleFactor, topOffset) {
    // Calculate scaled dimensions preserving the lock's aspect ratio
    const imgAspect = img.width / img.height;
    
    // Scale based on canvas width but maintain the lock's natural aspect ratio
    const scaledWidth = canvasWidth * scaleFactor;
    const scaledHeight = scaledWidth / imgAspect; // Preserve aspect ratio
    
    // Center horizontally
    const x = (canvasWidth - scaledWidth) / 2;
    
    // Calculate vertical offset from center
    let y = (canvasHeight - scaledHeight) / 2; // Default center
    
    // Parse and apply top offset (e.g., "0%", "-9%", "2%")
    if (topOffset && topOffset.includes('%')) {
      const offsetPercent = parseFloat(topOffset);
      // Apply offset relative to canvas height
      y = y + ((offsetPercent / 100) * canvasHeight);
    }
    
    console.log('🔒 Lock dimensions: img', img.width, 'x', img.height, '-> scaled', scaledWidth.toFixed(0), 'x', scaledHeight.toFixed(0));
    ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
  }

  /**
   * Draw an image on the canvas
   */
  async drawImage(ctx, imageUrl, x, y, width, height, flipHorizontal = false) {
    return new Promise((resolve, reject) => {
      // Check cache first
      if (this.imageCache.has(imageUrl)) {
        const img = this.imageCache.get(imageUrl);
        
        if (flipHorizontal) {
          // Apply horizontal flip transformation
          ctx.save();
          ctx.translate(width, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(img, x, y, width, height);
          ctx.restore();
        } else {
          ctx.drawImage(img, x, y, width, height);
        }
        
        resolve();
        return;
      }

      // Load new image
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        this.imageCache.set(imageUrl, img);
        
        if (flipHorizontal) {
          // Apply horizontal flip transformation
          ctx.save();
          ctx.translate(width, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(img, x, y, width, height);
          ctx.restore();
        } else {
          ctx.drawImage(img, x, y, width, height);
        }
        
        resolve();
      };
      
      img.onerror = (err) => {
        console.warn('⚠️ Failed to load image:', imageUrl, err);
        resolve(); // Don't reject, just skip this layer
      };
      
      img.src = imageUrl;
    });
  }

  /**
   * Draw a texture image masked by a mask image (like CSS mask-image)
   * This replicates the DOM-based masking system from product-configurator.js
   * Using multiply blend at 0.85 opacity (or 0.9 for Rimrock) to match configurator
   */
  async drawMaskedTexture(ctx, maskUrl, textureUrl, width, height) {
    if (!maskUrl || !textureUrl) return;

    return new Promise(async (resolve) => {
      try {
        // Load both images
        const [maskImg, textureImg] = await Promise.all([
          this.loadImage(maskUrl),
          this.loadImage(textureUrl)
        ]);

        if (!maskImg || !textureImg) {
          resolve();
          return;
        }

        // Check if this is Rimrock texture (slightly less transparent than others)
        const isRimrock = textureUrl.toLowerCase().includes('rimrock');
        const textureOpacity = isRimrock ? 0.7 : 0.65; // Reduced for better detail visibility

        // Create a temporary canvas for the masking operation
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = width;
        tempCanvas.height = height;
        const tempCtx = tempCanvas.getContext('2d');

        // Draw the texture
        tempCtx.drawImage(textureImg, 0, 0, width, height);

        // Use the mask as an alpha mask (destination-in composite)
        tempCtx.globalCompositeOperation = 'destination-in';
        tempCtx.drawImage(maskImg, 0, 0, width, height);

        // Draw the masked result onto the main canvas with multiply blend
        // This matches product-configurator.js: mixBlendMode = 'multiply', opacity = 0.85/0.9
        ctx.globalCompositeOperation = 'multiply';
        ctx.globalAlpha = textureOpacity;
        ctx.drawImage(tempCanvas, 0, 0, width, height);

        // Reset composite operation and alpha
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 1.0;

        resolve();
      } catch (error) {
        console.warn('⚠️ Failed to draw masked texture:', error);
        resolve();
      }
    });
  }

  /**
   * Draw a mask filled with a solid color (for gloss finish colors)
   * This replicates the DOM-based color masking from product-configurator.js
   * TWO-LAYER APPROACH:
   * 1. First draw the mask as a visible texture/detail layer
   * 2. Then apply color through the mask with multiply blend at 0.7 opacity
   */
  async drawColoredMask(ctx, maskUrl, colorCode, width, height, flipHorizontal = false) {
    if (!maskUrl || !colorCode) return;

    return new Promise(async (resolve) => {
      try {
        // Load the mask image
        const maskImg = await this.loadImage(maskUrl);
        if (!maskImg) {
          resolve();
          return;
        }

        // Get the hex color for this color code (from manifest if available)
        const hexColor = this.getColorHex(colorCode, this.currentManifest);

        // LAYER 1: Draw the mask itself as a visible texture layer (shows details)
        // This matches: this.addImageLayer(container, bodyMaskUrl, 2, 'body-texture', 1);
        if (flipHorizontal) {
          ctx.save();
          ctx.translate(width, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(maskImg, 0, 0, width, height);
          ctx.restore();
        } else {
          ctx.drawImage(maskImg, 0, 0, width, height);
        }
        console.log('  ↳ Drew mask base layer for texture/details');

        // LAYER 2: Create a temporary canvas for the color overlay
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = width;
        tempCanvas.height = height;
        const tempCtx = tempCanvas.getContext('2d');

        // Fill with the solid color
        tempCtx.fillStyle = hexColor;
        tempCtx.fillRect(0, 0, width, height);

        // Use the mask as an alpha mask (destination-in composite)
        tempCtx.globalCompositeOperation = 'destination-in';
        tempCtx.drawImage(maskImg, 0, 0, width, height);

        // Draw the colored mask onto the main canvas with multiply blend at 0.7 opacity
        // This matches: this.addColorMaskLayer(container, bodyMaskUrl, bodyColor, 3, 'body', 'multiply', '0.7');
        ctx.globalCompositeOperation = 'multiply';
        ctx.globalAlpha = 0.7; // Match product configurator's default opacity
        
        if (flipHorizontal) {
          ctx.save();
          ctx.translate(width, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(tempCanvas, 0, 0, width, height);
          ctx.restore();
        } else {
          ctx.drawImage(tempCanvas, 0, 0, width, height);
        }

        // Reset composite operation and alpha
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 1.0;

        console.log('  ↳ Applied color overlay with multiply blend at 70% opacity');

        resolve();
      } catch (error) {
        console.warn('⚠️ Failed to draw colored mask:', error);
        resolve();
      }
    });
  }

  /**
   * Get hex color code from color name
   * Gets colors from manifest.colors (from Option Value metaobjects) or falls back to defaults
   */
  getColorHex(colorCode, manifest) {
    // First try to get from manifest colors (dynamically loaded from option values)
    if (manifest?.colors && manifest.colors[colorCode]) {
      return manifest.colors[colorCode];
    }

    // Fallback color map for backwards compatibility
    const fallbackColorMap = {
      // Gloss finish colors (color_single, body_color, door_color)
      'BRILLIANT_WHITE': '#FFFFFF',
      'GLOSS_BLACK': '#000000',
      'JET_BLACK': '#000000',
      'FOREST_GREEN': '#1a453c', // Updated to match your metaobject
      'COPPER_BROWN': '#B87333',
      'CHAMPAGNE': '#F7E7CE',
      'MERCEDES_SILVER': '#C0C0C0',
      'BURGUNDY': '#800020',
      'COBALT_BLUE': '#0047AB',
      'HUNTER_GREEN': '#355E3B',
      'CHARCOAL_GRAY': '#36454F',
      'BLACK_CHERRY': '#3D0C02',
      
      // Lock/Graphic/Hardware colors
      'GOLD': '#FFD700',
      'SILVER': '#C0C0C0',
      'BRONZE': '#CD7F32',
      'BLACK': '#000000',
      'CHROME': '#E5E5E5',
      'SATIN_CHROME': '#C0C0C0',
      'BRUSHED_NICKEL': '#A9A9A9',
      
      // Textured finish colors (if needed)
      'FOREST_GREEN_TEXTURED': '#1a453c',
      'COPPER_BROWN_TEXTURED': '#B87333',
      'CHARCOAL_GRAY_TEXTURED': '#36454F'
    };

    const color = fallbackColorMap[colorCode];
    if (!color) {
      console.warn('⚠️ Color not found:', colorCode, '- using default gray');
      return '#808080'; // Default to gray if color not found
    }
    return color;
  }

  /**
   * Calculate scale factor for accessories/locks based on safe size
   * Matches product configurator's calculateAccessoryScale method
   */
  calculateAccessoryScale(currentSizeCode, referenceSizeCode) {
    // Parse current size dimensions
    const currentMatch = currentSizeCode.match(/(\d+)x(\d+)(?:x(\d+))?/);
    if (!currentMatch) return 1;
    
    const currentHeight = parseInt(currentMatch[1]);
    const currentWidth = parseInt(currentMatch[2]);
    
    // Parse reference size dimensions
    const refMatch = referenceSizeCode.match(/(\d+)x(\d+)(?:x(\d+))?/);
    if (!refMatch) return 1;
    
    const refHeight = parseInt(refMatch[1]);
    const refWidth = parseInt(refMatch[2]);
    
    // Calculate scale based on height ratio (larger safes = smaller accessories)
    const heightRatio = currentHeight / refHeight;
    const scaleFactor = 1 / Math.pow(heightRatio, 0.3); // Gentle scaling curve
    
    return scaleFactor;
  }

  /**
   * Helper to load an image and return a promise
   */
  async loadImage(imageUrl) {
    return new Promise((resolve) => {
      if (this.imageCache.has(imageUrl)) {
        resolve(this.imageCache.get(imageUrl));
        return;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        this.imageCache.set(imageUrl, img);
        resolve(img);
      };
      
      img.onerror = (err) => {
        console.warn('⚠️ Failed to load image:', imageUrl, err);
        resolve(null);
      };
      
      img.src = imageUrl;
    });
  }

  /**
   * Replace the cart item image with the rendered one
   */
  replaceCartImage(itemKey, dataUrl) {
    // Find the image by data-cart-item-key attribute
    const imgElement = document.querySelector(`img.cart-configured-safe-image[data-cart-item-key="${itemKey}"]`);
    
    if (!imgElement) {
      console.warn('⚠️ Could not find cart image element for key:', itemKey);
      return;
    }

    // Replace the image source
    imgElement.src = dataUrl;
    imgElement.srcset = ''; // Clear srcset to prevent Shopify from overriding
    imgElement.dataset.rendered = 'true'; // Prevents re-rendering this element on later scans

    // Update the badge
    const badge = imgElement.parentElement.querySelector('.configured-badge');
    if (badge) {
      badge.textContent = '✓ Custom Configuration';
      badge.style.color = '#059669'; // Green color
    }
    
    console.log('✅ Replaced cart image for item:', itemKey);
  }

  /**
   * Alternative method to replace cart image when item element is not found
   */
  replaceCartImageAlternative(itemKey, dataUrl) {
    // Deprecated - not needed with data-cart-item-key attribute
    console.warn('⚠️ Using alternative image replacement (should not be needed)');
  }
}

// Single shared instance so the image cache (decoded layer images) persists across renders —
// the ajax cart drawer can re-open and re-render the same items many times in one session.
const cartConfiguredImageRenderer = new CartConfiguredImageRenderer();

// Not scoped to the /cart page: the ajax cart drawer (templates/cart.ajax.liquid) can render
// on any page after an add-to-cart, and previously this renderer skipped entirely outside
// /cart, leaving the raw placeholder product image visible in the drawer.
async function renderConfiguredCartImages() {
  try {
    const cartResponse = await fetch('/cart.js');
    const cartData = await cartResponse.json();
    await cartConfiguredImageRenderer.renderCartImages(cartData);
  } catch (error) {
    console.error('❌ Error rendering configured cart images:', error);
  }
}

document.addEventListener('DOMContentLoaded', renderConfiguredCartImages);
document.addEventListener('cart:updated', function () {
  setTimeout(renderConfiguredCartImages, 300);
});
document.addEventListener('cart:refresh', function () {
  setTimeout(renderConfiguredCartImages, 300);
});

// Cart drawer content can be injected well after DOMContentLoaded (e.g. opened via ajax
// fetch of cart.ajax.liquid, or updated in place after add-to-cart) — watch for that content
// appearing and re-run. Mirrors configurator-cart-price-display.js's observer.
const cartImageObserver = new MutationObserver(function (mutations) {
  const added = mutations.some(function (m) {
    return m.addedNodes.length > 0;
  });
  if (added) {
    clearTimeout(window.__configuratorCartImageDebounce);
    window.__configuratorCartImageDebounce = setTimeout(renderConfiguredCartImages, 300);
  }
});
document.addEventListener('DOMContentLoaded', function () {
  cartImageObserver.observe(document.body, { childList: true, subtree: true });
});

// Export for use in other scripts
window.CartConfiguredImageRenderer = CartConfiguredImageRenderer;
window.renderConfiguredCartImages = renderConfiguredCartImages;
